import { Box } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React from "react";
import type { RenderQrCodeNode as RenderQrCodeNodeModel } from "../../../util/src/layout-compiler/types.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderQrCodeNodeProps = {
  node: RenderQrCodeNodeModel;
  scale: number;
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

function toQrSvgColor(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : fallback;
}

function resolveQrCodeRenderSize(
  width: number,
  height: number,
  scale: number,
): number {
  const w = Number.isFinite(width) ? width * scale : 100;
  const h = Number.isFinite(height) ? height * scale : 100;
  return Math.max(1, Math.round(Math.min(w, h)));
}

export function RenderQrCodeNode({ node, scale }: RenderQrCodeNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const clipStyle = getRenderNodeClipStyle(node, scale);
  const value = String(node.value ?? "");
  const bgColor = toQrSvgColor(node.style.backgroundColor, "#ffffff");
  const fgColor = toQrSvgColor(node.style.color, "#000000");
  const size = resolveQrCodeRenderSize(node.width, node.height, scale);

  return (
    <Box
      {...runtimeProps}
      sx={{
        ...(node.style.position === "relative"
          ? {
              position: "relative",
              flexShrink: 0,
            }
          : {
              position: "absolute",
              left: node.x * scale,
              top: node.y * scale,
            }),
        width: node.width * scale,
        height: node.height * scale,
        zIndex: node.zIndex,
        opacity: node.style.opacity as number | undefined,
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(node.style as React.CSSProperties),
        transform: composeTransform(node.rotation, node.style?.transform),
        transformOrigin: "center center",
        ...clipStyle,
      }}
    >
      {value.trim().length > 0 ? (
        <QRCodeSVG
          value={value}
          size={size}
          bgColor={bgColor}
          fgColor={fgColor}
        />
      ) : null}
    </Box>
  );
}
